import { memo, useEffect, useRef, useState } from "react";
import { Skeleton } from "antd";
import { Clapperboard, ExternalLink, Megaphone } from "lucide-react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import type { AdInfo } from "../../api/ChannelMessage";
import MessageBubble from "./MessageBubble";
import type { ChatMessage } from "./types";

/** Hàm dịch của react-i18next (chỉ cần khoá + tham số nội suy) */
type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * Thẻ cho attachment "story_mention" — khách nhắc page trong tin của họ.
 * Payload chỉ có url media trên CDN của Meta, không kèm tiêu đề/ảnh thu nhỏ, và link
 * có chữ ký nên sẽ hết hạn: phát được thì hiện video, hỏng thì rơi về link mở tab mới.
 */
function StoryMentionCard({ url, label, openText, expiredText }: {
    url: string; label: string; openText: string; expiredText: string;
}) {
    const [failed, setFailed] = useState(false);
    const [ready, setReady] = useState(false);
    const videoRef = useRef<HTMLVideoElement>(null);

    // Link hết hạn thì trình duyệt treo ở trạng thái đang tải chứ KHÔNG bắn sự kiện error,
    // để vậy người dùng nhìn mãi khung đen. Quá 8 giây chưa có metadata thì coi như hỏng.
    useEffect(() => {
        if (ready || failed) return;
        const timer = setTimeout(() => {
            if ((videoRef.current?.readyState ?? 0) === 0) setFailed(true);
        }, 8000);
        return () => clearTimeout(timer);
    }, [ready, failed]);

    return (
        <div className="flex justify-start mb-2">
            <div className="ml-10 max-w-[340px] w-full rounded-xl border border-gray-200 dark:border-[#333] bg-white dark:bg-[#262626] overflow-hidden shadow-sm">
                <div className="px-3 py-1.5 text-[11px] text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-[#333] flex items-center gap-1">
                    <Clapperboard size={12} className="text-[#3e91ab]" />
                    {label}
                </div>

                {!failed ? (
                    <video
                        ref={videoRef}
                        src={url}
                        controls
                        playsInline
                        preload="metadata"
                        onLoadedMetadata={() => setReady(true)}
                        onError={() => setFailed(true)}
                        className="w-full max-h-[320px] bg-black"
                    />
                ) : (
                    <div className="px-3 py-2 text-[12px] text-gray-500 dark:text-gray-400">
                        {expiredText}
                    </div>
                )}

                <div className="px-3 py-2 border-t border-gray-100 dark:border-[#333]">
                    <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-[11px] text-[#3e91ab] hover:underline"
                    >
                        <ExternalLink size={11} />
                        {openText}
                    </a>
                </div>
            </div>
        </div>
    );
}

/** Thẻ "Đến từ quảng cáo": bài quảng cáo / bài viết khách bấm vào để nhắn */
function AdCard({ ad, t }: { ad: AdInfo; t: Translate }) {
    const adTitle = ad.ad_name || ad.creative_name;
    return (
        <div className="flex justify-start mb-2">
            <div className="ml-10 max-w-[340px] w-full rounded-xl border border-gray-200 dark:border-[#333] bg-white dark:bg-[#262626] overflow-hidden shadow-sm">
                <div className="px-3 py-1.5 text-[11px] text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-[#333] flex items-center gap-1">
                    <Megaphone size={12} className="text-[#3e91ab]" />
                    {t("roomchat.from_ad")}
                </div>
                <div className="flex gap-2 p-2">
                    <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-gray-700 flex-shrink-0 flex items-center justify-center overflow-hidden relative">
                        <Megaphone size={18} className="text-gray-400 absolute" />
                        {ad.ad_image && (
                            <img
                                src={ad.ad_image}
                                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                                className="w-full h-full object-cover relative"
                                alt=""
                            />
                        )}
                    </div>
                    <div className="min-w-0 flex-1">
                        {adTitle && (
                            <div className="text-[13px] font-medium text-gray-800 dark:text-gray-100 line-clamp-2">
                                {adTitle}
                            </div>
                        )}
                        {ad.post_title && (
                            <div className={`line-clamp-2 mt-0.5 ${adTitle
                                ? "text-[12px] text-gray-600 dark:text-gray-300"
                                : "text-[13px] font-medium text-gray-800 dark:text-gray-100"
                                }`}>
                                {ad.post_title}
                            </div>
                        )}
                        {ad.campaign_name && (
                            <div className="text-[10px] text-gray-400 line-clamp-1 mt-0.5">
                                {t("roomchat.ad_campaign")}: {ad.campaign_name}
                            </div>
                        )}
                        {ad.ad_id && (
                            <div className="text-[10px] text-gray-400 mt-0.5 truncate">
                                ID: {ad.ad_id}
                            </div>
                        )}
                        {ad.post_url && (
                            <a
                                href={ad.post_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 text-[11px] text-[#3e91ab] hover:underline mt-1"
                            >
                                <ExternalLink size={11} />
                                {t("roomchat.ad_view_post")}
                            </a>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

/** Mốc thời gian chia đoạn hội thoại: khác ngày, hoặc cách tin trước từ 12 tiếng */
const shouldShowDivider = (currentTime: string, prevTime?: string) => {
    if (!prevTime) return true;
    const current = dayjs(currentTime);
    const prev = dayjs(prevTime);
    if (!current.isSame(prev, "day")) return true;
    return current.diff(prev, "hour") >= 12;
};

const getDividerLabel = (time: string, t: Translate) => {
    const date = dayjs(time);
    if (date.isSame(dayjs(), "day")) {
        return t("roomchat.divider_today", { time: date.format("HH:mm") });
    }
    if (date.isSame(dayjs().subtract(1, "day"), "day")) {
        return t("roomchat.divider_yesterday", { time: date.format("HH:mm") });
    }
    return date.format("HH:mm DD/MM/YYYY");
};

interface Props {
    messages: ChatMessage[];
    loading: boolean;
    /** Ảnh đại diện tài khoản đang đăng nhập (tin của mình) */
    myAvatar?: string;
    /** Ảnh đại diện khách (tin khách gửi) */
    customerAvatar?: string;
    /** Tin gửi lỗi: gửi lại / bỏ (nên truyền hàm ổn định để memo có tác dụng) */
    onRetry: (id: number | string) => void;
    onDelete: (id: number | string) => void;
}

/**
 * Danh sách tin của khung chat. Bọc memo: chỉ dựng lại khi danh sách tin đổi, gõ phím ở
 * ô soạn tin hay mở popover ở header không phải dựng lại hàng trăm bong bóng tin.
 */
function MessageList({ messages, loading, myAvatar, customerAvatar, onRetry, onDelete }: Props) {
    const { t } = useTranslation();

    if (loading) {
        return (
            <div className="flex flex-col gap-4">
                {Array.from({ length: 6 }).map((_, idx) => {
                    const isMe = idx % 2 === 1;
                    return (
                        <div
                            key={`msg-skeleton-${idx}`}
                            className={`flex items-end gap-2 ${isMe ? "flex-row-reverse" : "flex-row"}`}
                        >
                            <Skeleton.Avatar active size={36} shape="circle" />
                            <Skeleton.Button
                                active
                                size="large"
                                shape="round"
                                style={{ width: idx % 3 === 0 ? 220 : 150, height: 40 }}
                            />
                        </div>
                    );
                })}
            </div>
        );
    }

    return (
        <>
            {messages.map((msg, index) => {
                const prevMsg = messages[index - 1];
                const showDivider = shouldShowDivider(msg.rawTime, prevMsg?.rawTime);

                // Nhiều nhân viên cùng chat: hiện tên người gửi ở tin đầu mỗi chuỗi
                // tin liên tiếp của cùng một người (giống Messenger)
                const sameSenderAsPrev = !!prevMsg && prevMsg.isMe && !showDivider
                    && (prevMsg.createdByName || "") === (msg.createdByName || "");
                const staffName = msg.isMe && msg.createdByName && !sameSenderAsPrev
                    ? msg.createdByName
                    : undefined;

                return (
                    <div key={msg.id}>
                        {showDivider && (
                            <div className="flex justify-center my-4">
                                <span className="px-4 py-1 rounded-full bg-[#bcbcbc] text-white text-[12px] shadow-sm">
                                    {getDividerLabel(msg.rawTime, t)}
                                </span>
                            </div>
                        )}

                        {msg.storyUrl && (
                            <StoryMentionCard
                                url={msg.storyUrl}
                                label={t("roomchat.story_mention")}
                                openText={t("roomchat.story_open")}
                                expiredText={t("roomchat.story_expired")}
                            />
                        )}

                        {(msg.ads || []).map((ad, adIndex) => (
                            <AdCard key={adIndex} ad={ad} t={t} />
                        ))}

                        <MessageBubble
                            text={msg.text}
                            sender={msg.sender}
                            staffName={staffName}
                            time={msg.time}
                            isMe={msg.isMe}
                            avatar={msg.isMe ? myAvatar : customerAvatar}
                            images={msg.images}
                            audios={msg.audios}
                            videos={msg.videos}
                            pending={msg.pending}
                            failed={msg.failed}
                            onRetry={msg.failed && msg.draft ? () => onRetry(msg.id) : undefined}
                            onDelete={msg.failed ? () => onDelete(msg.id) : undefined}
                        />
                    </div>
                );
            })}
        </>
    );
}

export default memo(MessageList);
